import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSearchParams } from "react-router-dom";
import { ImageConfig } from "@/constants";
import { CommonService } from "@/services";
import commonService from "@/services/common.service.ts";
import { useSocket } from "@/services/hooks/useSocket.ts";
import { updateProjectDetails } from "@/store/actions/projectdata.action.ts";
import { IRootReducerState } from "@/store/reducers";
import { Check, ChevronDown, Copy, Globe } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./PublishPopoverComponent.scss";
import { AddIcon } from "@/constants/ImageConfig";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { FeatureRestrictionWrapper } from "@/components/common-components/feature-restriction";
import { useIsMobile } from "@/hooks/use-mobile.ts";

interface PublishPopoverComponentProps {
    projectId: string;
    appType: string;
    onPublishSuccess?: () => void;
    onOpenCustomDomainSettings?: () => void;
    isMobile?: boolean;
    showAsTag?: boolean;
}

enum DeployEventType {
    DEPLOY_STARTED = "deploy.started",
    DEPLOY_DELTA = "deploy.delta",
    DEPLOY_SUCCESS = "deploy.success",
    DEPLOY_DNS_SUCCESS = "deploy.dns.success",
    DEPLOY_DNS_ERROR = "deploy.dns.error",
    DEPLOY_ERROR = "deploy.error"
}



const PublishPopoverComponent = ({ projectId, appType, onPublishSuccess, showAsTag = false }: PublishPopoverComponentProps) => {
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const [, setSearchParams] = useSearchParams();
    const currentProjectDetails = useSelector((state: IRootReducerState) => state.projectData.currentProjectDetails);

    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deploymentProgress, setDeploymentProgress] = useState(0);
    const [deploymentMessage, setDeploymentMessage] = useState("");
    const [, setDeployedSubdomain] = useState("");
    const [copySuccess, setCopySuccess] = useState("");
    const [hasProcessedSuccess, setHasProcessedSuccess] = useState(false);

    const isPublished = !!currentProjectDetails?.subdomain_url;


    // Initialize socket hook
    const { events } = useSocket({
        eventTypes: [
            DeployEventType.DEPLOY_STARTED,
            DeployEventType.DEPLOY_DELTA,
            DeployEventType.DEPLOY_SUCCESS,
            DeployEventType.DEPLOY_DNS_SUCCESS,
            DeployEventType.DEPLOY_ERROR,
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'DeploymentSocket',
        debug: false,
    });

    // Handle socket events for deployment status updates
    useEffect(() => {
        // Handle deployment started event
        if (events[DeployEventType.DEPLOY_STARTED]) {
            setDeploymentProgress(10);
            setDeploymentMessage("Deployment started. Building your application...");

            // Track deployment progress start
            commonService.handleMixpanelTrack("deployment_progress_started", {
                project_id: projectId,
                app_type: appType,
                is_mobile: isMobile,
                deployment_type: isPublished ? "update" : "publish"
            });
        }

        // Handle deployment progress updates
        if (events[DeployEventType.DEPLOY_DELTA]) {
            const deltaEvent = events[DeployEventType.DEPLOY_DELTA];
            const deltaEventData = deltaEvent?.event_meta;
            const message = deltaEventData?.msg || "Deploying your application...";

            // Safely parse progress percentage
            const progressPercentage = deltaEventData?.progress_percentage;
            if (progressPercentage && !isNaN(parseInt(progressPercentage))) {
                setDeploymentProgress(parseInt(progressPercentage));
            }

            setDeploymentMessage(message);
        }

        // Handle deployment success
        if (events[DeployEventType.DEPLOY_SUCCESS]) {
            const successEvent = events[DeployEventType.DEPLOY_SUCCESS];
            const successEventData = successEvent?.event_meta;
            setDeploymentProgress(95);
            setDeploymentMessage("Application deployed successfully. Configuring DNS...");

            // Extract subdomain from the event if available
            if (successEventData?.subdomain) {
                setDeployedSubdomain(successEvent.event_meta.subdomain);
            }
        }

        // Handle DNS configuration success
        if (events[DeployEventType.DEPLOY_DNS_SUCCESS] && !hasProcessedSuccess) {
            const dnsSuccessEvent = events[DeployEventType.DEPLOY_DNS_SUCCESS];
            const dnsSuccessEventData = dnsSuccessEvent?.event_meta;
            setDeploymentProgress(100);
            setDeploymentMessage("Deployment complete! Your application is now available.");
            setIsSubmitting(false);
            setHasProcessedSuccess(true);

            // Extract subdomain URL from the event
            const subdomainUrl = dnsSuccessEventData?.subdomain_url ||
                (dnsSuccessEventData?.subdomain ? `https://${dnsSuccessEventData?.subdomain}.superengineer.ai` : "");

            // Track deployment success event
            commonService.handleMixpanelTrack("deployment_success", {
                project_id: projectId,
                app_type: appType,
                subdomain_url: subdomainUrl,
                subdomain: dnsSuccessEventData?.subdomain,
                is_mobile: isMobile,
                deployment_type: isPublished ? "update" : "publish"
            });

            // Update project details in the store for both add and edit modes
            dispatch(updateProjectDetails({
                subdomain_url: subdomainUrl,
                subdomain: dnsSuccessEventData?.subdomain
            }));

            // Notify parent component about successful deployment
            if (subdomainUrl) {
                onPublishSuccess?.();
                commonService._alert.showToast(
                    "App published successfully",
                    "success"
                );
                // Close popover after successful deployment
                setIsOpen(false);
            }
        }

        // Handle deployment error
        if (events[DeployEventType.DEPLOY_ERROR] || events[DeployEventType.DEPLOY_DNS_ERROR]) {
            const errorEvent = events[DeployEventType.DEPLOY_ERROR] || events[DeployEventType.DEPLOY_DNS_ERROR];
            setDeploymentMessage("Deployment failed. Please try again.");
            setIsSubmitting(false);

            // Safely extract error message
            let errorMessage = "Deployment failed";
            if (errorEvent?.event_meta?.error) {
                errorMessage = errorEvent.event_meta.error;
            } else if (errorEvent?.error) {
                errorMessage = errorEvent.error;
            } else if (typeof errorEvent === 'string') {
                errorMessage = errorEvent;
            }

            // Track deployment error event from socket
            commonService.handleMixpanelTrack("deployment_error", {
                project_id: projectId,
                app_type: appType,
                error_message: errorMessage,
                is_mobile: isMobile,
                deployment_type: isPublished ? "update" : "publish",
                error_source: "socket_event"
            });

            commonService._alert.showToast(errorMessage, "error");
        }
    }, [
        events,
        dispatch,
        onPublishSuccess,
        hasProcessedSuccess,
        projectId,
        appType,
        isMobile,
        isPublished
    ]);

    const handlePublishApp = useCallback(async () => {
        setIsSubmitting(true);
        setDeploymentProgress(0);
        setHasProcessedSuccess(false); // Reset the success flag when starting new deployment

        // Track deployment start event
        commonService.handleMixpanelTrack("deployment_started", {
            project_id: projectId,
            app_type: appType,
            is_published: isPublished,
            is_mobile: isMobile,
            deployment_type: isPublished ? "update" : "publish"
        });

        try {
            // Call the deployment API
            commonService._project_service.deployApp(projectId, {
            }).catch((error: any) => {
                setIsSubmitting(false);
                const errorMessage = error?.detail || error?.message || "An error occurred during publishing";

                // Track deployment error event
                commonService.handleMixpanelTrack("deployment_error", {
                    project_id: projectId,
                    app_type: appType,
                    error_message: errorMessage,
                    is_mobile: isMobile,
                    deployment_type: isPublished ? "update" : "publish"
                });

                commonService._alert.showToast(errorMessage, "error");
            });
        } catch (error: any) {
            setIsSubmitting(false);
            const errorMessage = error?.detail || error?.message || "An error occurred";

            // Track deployment error event
            commonService.handleMixpanelTrack("deployment_error", {
                project_id: projectId,
                app_type: appType,
                error_message: errorMessage,
                is_mobile: isMobile,
                deployment_type: isPublished ? "update" : "publish"
            });

            commonService._alert.showToast(errorMessage, "error");
        }
    }, [projectId, appType, isPublished, isMobile]);

    const handleCopyUrl = async () => {
        const url = currentProjectDetails?.subdomain_url || "";
        try {
            await navigator.clipboard.writeText(url);
            setCopySuccess('copied');
            setTimeout(() => setCopySuccess(''), 1000);
        } catch (error) {
            console.error('Failed to copy link:', error);
            commonService._alert.showToast("Failed to copy link", "error");
        }
    };

    const handleShare = (platform: string) => {
        const url = currentProjectDetails?.subdomain_url || "";
        const message = `Check out my new ${CommonService.capitalizeFirstLetter(appType)} app, built with superengineer.ai: ${url} 🎉 Feel free to explore and spread the word!`;

        let shareUrl = "";

        switch (platform) {
            case "twitter":
                // X (Twitter) - URL and text prepopulated
                shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`;
                break;

            case "linkedin":
                // LinkedIn - Use feed share with text parameter for prepopulation
                shareUrl = `https://www.linkedin.com/feed/?shareActive=true&mini=true&text=${encodeURIComponent(message)}`;
                break;

            case "facebook":
                // Facebook share dialog - URL only (Facebook removed quote parameter)
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
                break;

            case "whatsapp":
                // WhatsApp message share - full message with URL
                shareUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                break;
        }

        if (shareUrl) {
            window.open(shareUrl, "_blank", "noopener,noreferrer");
        }
    };



    const getButtonText = () => {
        if (isSubmitting) {
            return "";
        }
        return isPublished ? "Update" : "Publish";
    };

    const renderTrigger = () => {
        if (showAsTag) {
            return (
                <div
                    className="publish-tag absolute flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <Globe className="w-3 h-3" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                        {isPublished ? "Update" : "Publish"}
                    </span>
                </div>
            );
        }

        return (
            <Button
                size="sm"
                variant={isMobile ? "outline" : "default"}
                className={isMobile && !showAsTag ? "h-8 w-8 p-0" : "flex items-center gap-2"}
                mixpanelEvent={{
                    name: "publish_popover_opened",
                    properties: {
                        project_id: projectId,
                        app_type: appType,
                        is_published: isPublished,
                        is_mobile: isMobile
                    }
                }}
            >
                {isMobile && !showAsTag ? (
                    <Globe className="w-4 h-4" />
                ) : (
                    <>
                        {isPublished ? "Update" : "Publish"}
                        <ChevronDown className="w-4 h-4" />
                    </>
                )}
            </Button>
        );
    };

    const renderContent = () => (
        <div className="publish-popover">
            <div className="publish-header">
                <h3 className="publish-title">Publish your project</h3>
            </div>
            {/* Simple status text above footer */}
            <div className="publish-status-text">
                {!isPublished ? (
                    <>
                        <p className="status-title">Ready to publish</p>
                        <p className="status-description">Your app will be deployed with a unique URL</p>
                    </>
                ) : (
                    <>
                        <p className="status-title">App is live</p>
                        <p className="status-description">Your app is published and accessible to users</p>
                    </>
                )}
            </div>
            {isPublished && (
                <div className="publish-content">
                    <div className="publish-url">
                        <div className="url-display">
                            <Globe className="w-4 h-4" />
                            <span
                                className="url-text"
                                onClick={() => {
                                    commonService.handleMixpanelTrack("published_app_url_clicked", {
                                        project_id: projectId,
                                        app_type: appType,
                                        url: currentProjectDetails?.subdomain_url,
                                        is_mobile: isMobile
                                    });
                                    window.open(currentProjectDetails?.subdomain_url, '_blank', 'noopener,noreferrer');
                                }}
                                style={{ cursor: 'pointer' }}
                            >{currentProjectDetails?.subdomain_url}</span>
                            <Button
                                variant="icon"
                                size="sm"
                                onClick={handleCopyUrl}
                                className="copy-button"
                                data-copied={copySuccess ? "true" : "false"}
                                mixpanelEvent={{
                                    name: "published_app_url_copied",
                                    properties: {
                                        project_id: projectId,
                                        app_type: appType,
                                        url: currentProjectDetails?.subdomain_url
                                    }
                                }}
                            >
                                {copySuccess ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                    </div>

                    <div className="share-section">
                        <p className="share-title">Share it with friends and coworkers</p>
                        <div className="share-buttons">
                            <Button
                                size="md"
                                variant="outline"
                                onClick={() => handleShare('twitter')}
                                mixpanelEvent={{
                                    name: "published_app_shared",
                                    properties: {
                                        project_id: projectId,
                                        app_type: appType,
                                        platform: 'twitter',
                                        url: currentProjectDetails?.subdomain_url
                                    }
                                }}
                            >
                                <ImageConfig.XIcon className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="md"
                                onClick={() => handleShare('linkedin')}
                                mixpanelEvent={{
                                    name: "published_app_shared",
                                    properties: {
                                        project_id: projectId,
                                        app_type: appType,
                                        platform: 'linkedin',
                                        url: currentProjectDetails?.subdomain_url
                                    }
                                }}
                            >
                                <ImageConfig.LinkedinIcon className="w-6 h-6" />
                            </Button>

                        </div>
                    </div>

                    <div className="custom-domain-option">
                        <FeatureRestrictionWrapper
                            feature="custom_domain"
                            showUpgradePrompt={true}
                        >
                            <Button
                                onClick={() => {
                                    // Open settings dialog with domains tab selected using React aware setSearchParams
                                    setSearchParams({ settings: 'domains' });

                                    // Trigger settings opening via communication service
                                    CommonService._communications.openSettingsSubject.next({
                                        category: 'domains'
                                    });
                                }}
                                variant="secondary"
                                prefixIcon={<AddIcon className="w-4 h-4" />}
                                size="sm"
                                mixpanelEvent={{
                                    name: "add_custom_domain_clicked",
                                    properties: {
                                        project_id: projectId,
                                        app_type: appType,
                                        source: 'publish_popover'
                                    }
                                }}
                            >
                                Add custom domain
                            </Button>
                        </FeatureRestrictionWrapper>
                    </div>
                </div>
            )}

            {isSubmitting && (
                <div className="deployment-progress-section">
                    <div className="progress-header">
                        <span className="progress-percentage">{deploymentProgress}%</span>
                        <span className="progress-status">Publishing...</span>
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${deploymentProgress}%` }}
                        ></div>
                    </div>
                    <p className="progress-message">{deploymentMessage}</p>
                </div>
            )}

            <div className="publish-footer">
                <FeatureRestrictionWrapper
                    feature="advanced_deployment"
                    showUpgradePrompt={true}
                >
                    <Button
                        size="sm"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handlePublishApp();
                        }}
                        disabled={isSubmitting}
                        loading={isSubmitting}
                        mixpanelEvent={{
                            name: "publish_button_clicked",
                            properties: {
                                project_id: projectId,
                                app_type: appType,
                                is_published: isPublished,
                                is_mobile: isMobile,
                                button_text: getButtonText(),
                                deployment_type: isPublished ? "update" : "publish"
                            }
                        }}
                    >
                        {getButtonText()}
                    </Button>
                </FeatureRestrictionWrapper>
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <Drawer
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open);
                    if (!open) {
                        commonService.handleMixpanelTrack("publish_drawer_closed", {
                            project_id: projectId,
                            app_type: appType,
                            is_published: isPublished,
                            is_mobile: isMobile,
                            was_submitting: isSubmitting
                        });
                    }
                }}
            >
                <DrawerTrigger asChild>
                    {renderTrigger()}
                </DrawerTrigger>
                <DrawerContent className="p-0">
                    <div className="max-h-[85vh] overflow-y-auto w-full">
                        <div className="p-6">
                            {renderContent()}
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) {
                    commonService.handleMixpanelTrack("publish_popover_closed", {
                        project_id: projectId,
                        app_type: appType,
                        is_published: isPublished,
                        is_mobile: isMobile,
                        was_submitting: isSubmitting
                    });
                }
            }}
        >
            <PopoverTrigger asChild>
                {renderTrigger()}
            </PopoverTrigger>
            <PopoverContent
                className="w-80 p-0"
                align="end"
            >
                {renderContent()}
            </PopoverContent>
        </Popover>
    );
};

export default PublishPopoverComponent;
